import { isRecord } from 'lib/object/objectUtils'
import {
  readTranslatedString,
  readTrimmedString,
  readTrimmedStringArray,
} from 'loyalty/lib/labPayloadUtils'

import type {
  IsLoyaltyFeatureFlagEnabled,
  LoyaltyWhatsNewCard,
  LoyaltyWhatsNewImageOptions,
} from './types'

const FIT_VALUES = new Set(['bottom', 'center', 'left', 'right', 'top', 'none'])
const OBJECT_FIT_VALUES = new Set(['cover', 'contain'])
const GRAVITY_VALUES = new Set(['auto', 'center', 'east', 'north', 'northeast', 'northwest', 'south', 'southeast', 'southwest', 'west'])

function readEnumValue<T extends string>(value: unknown, allowed: ReadonlySet<string>): T | undefined {
  const trimmed = readTrimmedString(value)
  return trimmed && allowed.has(trimmed) ? (trimmed as T) : undefined
}

/** Reads only the whitelisted, validated image options; returns undefined when none are set. */
function readImageOptions(value: unknown): LoyaltyWhatsNewImageOptions | undefined {
  if (!isRecord(value)) return undefined

  const options: LoyaltyWhatsNewImageOptions = {}
  const fit = readEnumValue<NonNullable<LoyaltyWhatsNewImageOptions['fit']>>(value.fit, FIT_VALUES)
  if (fit) options.fit = fit
  const objectFit = readEnumValue<NonNullable<LoyaltyWhatsNewImageOptions['objectFit']>>(value.objectFit, OBJECT_FIT_VALUES)
  if (objectFit) options.objectFit = objectFit
  const gravity = readEnumValue<NonNullable<LoyaltyWhatsNewImageOptions['gravity']>>(value.gravity, GRAVITY_VALUES)
  if (gravity) options.gravity = gravity

  return Object.keys(options).length > 0 ? options : undefined
}

function isActiveForRegion(source: Record<string, unknown>, regionCode: string, now: Date): boolean {
  if (source.isActive !== true) return false

  // `enabledRegions` is an allow-list. Omitted or empty ⇒ all regions.
  const enabledRegions = readTrimmedStringArray(source.enabledRegions)
  if (enabledRegions && enabledRegions.length > 0 && !enabledRegions.includes(regionCode)) return false

  const startAt = readTrimmedString(source.startAt)
  const endAt = readTrimmedString(source.endAt)
  if (!startAt || !endAt) return false

  const startTime = new Date(startAt).getTime()
  const endTime = new Date(endAt).getTime()
  const nowTime = now.getTime()

  if (Number.isNaN(startTime) || Number.isNaN(endTime)) return false
  return startTime <= nowTime && nowTime <= endTime
}

export function mapLoyaltyWhatsNewCard(
  value: unknown,
  regionCode: string,
  now: Date,
  isFeatureFlagEnabled: IsLoyaltyFeatureFlagEnabled,
): LoyaltyWhatsNewCard | undefined {
  if (!isRecord(value) || !isActiveForRegion(value, regionCode, now)) return undefined

  // Optional per-card Optimizely gate.
  const optimizelyFeatureFlag = readTrimmedString(value.optimizelyFeatureFlag)
  if (optimizelyFeatureFlag && !isFeatureFlagEnabled(optimizelyFeatureFlag)) return undefined

  const overline = readTranslatedString(value, 'overline')
  const title = readTranslatedString(value, 'title')
  const description = readTranslatedString(value, 'description')
  const ctaLabel = readTranslatedString(value, 'ctaLabel')
  const ctaUrl = readTrimmedString(value.ctaUrl)
  const imageId = readTrimmedString(value.imageId)
  const startAt = readTrimmedString(value.startAt)
  const endAt = readTrimmedString(value.endAt)

  if (!overline || !title || !description || !ctaLabel || !ctaUrl || !imageId || !startAt || !endAt) return undefined

  return {
    overline,
    title,
    description,
    ctaLabel,
    ctaUrl,
    imageId,
    imageOptions: readImageOptions(value.imageOptions),
    startAt,
    endAt,
  }
}
